import { useEffect } from 'react';
import '@/components/landing/landing.css';
import { FooterSection, Navbar } from '@/components/landing';
import { navigateToLanding } from '@/lib/navigateLanding';

interface DatenschutzViewProps {
  onNavigateHome: () => void;
  onBlogClick?: () => void;
  onImpressumClick?: () => void;
}

export const DatenschutzView: React.FC<DatenschutzViewProps> = ({
  onNavigateHome,
  onBlogClick,
  onImpressumClick,
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const prev = document.title;
    document.title = 'Datenschutz — estage.ai';
    return () => { document.title = prev; };
  }, []);
  const handleNavigateHome = (sectionHash?: string) => navigateToLanding(onNavigateHome, sectionHash);

  return (
    <div className="landing-root">
      <Navbar onCtaClick={onNavigateHome} onLoginClick={onNavigateHome} onNavigateHome={handleNavigateHome} />

      {/* Content */}
      <div className="lp-legal-page">
        <div className="lp-legal-inner">
          <button onClick={onNavigateHome} className="lp-legal-back">← Zurück zur Startseite</button>
          <h1 className="lp-legal-title">Datenschutzerklärung</h1>
          <p className="lp-legal-updated">Stand: April 2026</p>

          <section className="lp-legal-section">
            <h2>1. Name und Anschrift des Verantwortlichen</h2>
            <p>
              Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO), des Bundesdatenschutz­
              gesetzes (BDSG) sowie weiterer datenschutzrechtlicher Bestimmungen ist:
            </p>
            <p>
              Mirey Advertising<br />
              Inhaber: Till Miosga<br />
              Thonberger Straße 3<br />
              04317 Leipzig<br />
              Deutschland<br />
              Telefon: <a href="tel:+4917623300414">+49 (0) 176 23300414</a><br />
              E-Mail: <a href="mailto:info@estage.ai">info@estage.ai</a>
            </p>
            <p>
              Ein Datenschutzbeauftragter ist gesetzlich nicht bestellt. Bei Fragen zum Datenschutz wenden
              Sie sich bitte an die oben genannte E-Mail-Adresse.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>2. Geltungsbereich</h2>
            <p>
              Diese Datenschutzerklärung gilt für die Website <strong>estage.ai</strong> sowie für alle
              darunter betriebenen Subdomains (z. B. <em>app.estage.ai</em>, <em>tryout.estage.ai</em>)
              und für den dort bereitgestellten Dienst zur KI-gestützten Bildbearbeitung von
              Immobilienfotografien („Dienst"). Für Websites Dritter, auf die wir verlinken, gilt
              ausschließlich deren Datenschutzerklärung.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>3. Allgemeines zur Datenverarbeitung</h2>
            <h3>3.1 Umfang der Verarbeitung</h3>
            <p>
              Wir erheben und verwenden personenbezogene Daten unserer Nutzer grundsätzlich nur, soweit
              dies zur Bereitstellung einer funktionsfähigen Website sowie unserer Inhalte und Leistungen
              erforderlich ist. Die Erhebung und Verwendung personenbezogener Daten erfolgt regelmäßig
              nur nach Einwilligung des Nutzers oder auf einer anderen in Art. 6 DSGVO vorgesehenen
              Rechtsgrundlage.
            </p>
            <h3>3.2 Rechtsgrundlagen</h3>
            <ul>
              <li><strong>Art. 6 Abs. 1 lit. a DSGVO</strong> — bei Einwilligung (z. B. Cookies, Analyse-Tools).</li>
              <li><strong>Art. 6 Abs. 1 lit. b DSGVO</strong> — zur Erfüllung eines Vertrags (z. B. Nutzerkonto, Bildbearbeitung, Zahlungsabwicklung).</li>
              <li><strong>Art. 6 Abs. 1 lit. c DSGVO</strong> — zur Erfüllung rechtlicher Verpflichtungen (z. B. steuerrechtliche Aufbewahrung).</li>
              <li><strong>Art. 6 Abs. 1 lit. f DSGVO</strong> — zur Wahrung berechtigter Interessen (z. B. Server-Logs, IT-Sicherheit, Missbrauchsprävention).</li>
            </ul>
            <h3>3.3 Speicherdauer</h3>
            <p>
              Personenbezogene Daten werden gelöscht, sobald der Zweck der Verarbeitung entfällt und
              keine gesetzlichen Aufbewahrungspflichten entgegenstehen. Steuerrelevante Unterlagen
              werden gemäß § 147 AO bzw. § 257 HGB bis zu zehn Jahre aufbewahrt.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>4. Bereitstellung der Website und Erstellung von Logfiles</h2>
            <p>
              Bei jedem Aufruf unserer Website erfasst unser System automatisiert Daten und Informationen
              vom Computersystem des aufrufenden Rechners. Folgende Daten werden hierbei erhoben:
            </p>
            <ul>
              <li>IP-Adresse (gekürzt bzw. anonymisiert, sofern technisch möglich)</li>
              <li>Datum und Uhrzeit des Zugriffs</li>
              <li>aufgerufene URL und Referrer-URL</li>
              <li>verwendeter Browser, Betriebssystem und Gerätetyp</li>
              <li>übertragene Datenmenge und HTTP-Statuscode</li>
            </ul>
            <p>
              <strong>Zweck:</strong> Bereitstellung, Stabilität und Sicherheit der Website.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an
              einem störungsfreien Betrieb und der IT-Sicherheit).<br />
              <strong>Speicherdauer:</strong> Logfiles werden in der Regel nach 14 Tagen gelöscht, es
              sei denn, sie sind für Beweiszwecke bei Sicherheitsvorfällen erforderlich.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>5. Cookies und vergleichbare Technologien</h2>
            <p>
              Wir verwenden auf unserer Website Cookies und ähnliche Technologien (z. B. Local Storage).
              Cookies sind kleine Textdateien, die über den Browser auf Ihrem Endgerät gespeichert werden.
            </p>
            <h3>5.1 Technisch notwendige Cookies</h3>
            <p>
              Diese Cookies sind erforderlich, damit die Website und zentrale Funktionen (z. B. Login,
              Warenkorb, Session-Handling) funktionieren. Rechtsgrundlage ist § 25 Abs. 2 Nr. 2 TDDDG
              i. V. m. Art. 6 Abs. 1 lit. f DSGVO. Für den Einsatz ist keine Einwilligung erforderlich.
            </p>
            <h3>5.2 Einwilligungspflichtige Cookies</h3>
            <p>
              Cookies und vergleichbare Technologien, die der Analyse, Reichweitenmessung oder
              Personalisierung dienen (z. B. Google Tag Manager, Google Analytics, Mixpanel), werden
              ausschließlich nach Ihrer Einwilligung gemäß § 25 Abs. 1 TDDDG i. V. m. Art. 6 Abs. 1
              lit. a DSGVO eingesetzt. Sie können Ihre Einwilligung jederzeit mit Wirkung für die Zukunft
              widerrufen, indem Sie die Cookie-Einstellungen im Footer der Seite anpassen oder die
              entsprechenden Cookies in Ihrem Browser löschen.
            </p>
            <h3>5.3 Browser-Einstellungen</h3>
            <p>
              Sie können Ihren Browser so einstellen, dass Cookies generell blockiert, nur in bestimmten
              Fällen akzeptiert oder beim Schließen des Browsers gelöscht werden. Bei Deaktivierung von
              Cookies kann die Funktionalität dieser Website eingeschränkt sein.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>6. Registrierung und Nutzerkonto</h2>
            <p>
              Zur Nutzung unseres Dienstes ist die Anlage eines Nutzerkontos erforderlich. Hierbei
              verarbeiten wir folgende Daten:
            </p>
            <ul>
              <li>E-Mail-Adresse</li>
              <li>Passwort (verschlüsselt gespeichert — wir haben keinen Zugriff im Klartext)</li>
              <li>Name und Unternehmensangaben (optional)</li>
              <li>Zugangsdaten aus Drittanbieter-Logins (sofern genutzt)</li>
              <li>Kontoeinstellungen, Branding-Daten und hochgeladene Logos</li>
            </ul>
            <p>
              <strong>Zweck:</strong> Bereitstellung und Verwaltung des Nutzerkontos, Zugang zu den
              gebuchten Leistungen, Support.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).<br />
              <strong>Speicherdauer:</strong> Für die Dauer der Vertragsbeziehung; anschließend bis zum
              Ablauf gesetzlicher Aufbewahrungsfristen.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>7. Verarbeitung hochgeladener Bilddaten</h2>
            <p>
              Im Rahmen des Dienstes laden Sie Immobilien-Fotografien hoch, die mittels Künstlicher
              Intelligenz virtuell möbliert, entrümpelt, aufgewertet oder hochskaliert werden können.
              Dabei werden folgende Daten verarbeitet:
            </p>
            <ul>
              <li>die hochgeladenen Bilder selbst</li>
              <li>daraus abgeleitete technische Metadaten (z. B. Bildmaße, Dateigröße)</li>
              <li>generierte Zwischen- und Ergebnisbilder</li>
            </ul>
            <p>
              Die Bilder werden in unserem Storage-System bei Supabase (Region: Frankfurt, EU) gespeichert
              und zur Verarbeitung an die KI-Dienste Google Gemini (Google LLC) sowie Freepik Magnific
              (Freepik Company, S.L.) übermittelt (siehe hierzu Ziffer 9).
            </p>
            <p>
              <strong>Zweck:</strong> Erbringung der gebuchten Bildbearbeitungsleistung.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).<br />
              <strong>Speicherdauer:</strong> Bis zur Löschung des Projekts durch den Nutzer, spätestens
              jedoch mit Beendigung des Nutzungsvertrags.
            </p>
            <p>
              <strong>Hinweis zu Personenbezug in Bildern:</strong> Sollten hochgeladene Bilder Personen
              erkennbar zeigen, sind Sie als Uploadender dafür verantwortlich, dass hierfür eine
              entsprechende Rechtsgrundlage (z. B. Einwilligung der abgebildeten Person nach KUG/DSGVO)
              vorliegt.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>8. Zahlungsabwicklung</h2>
            <p>
              Für die Zahlungsabwicklung nutzen wir den Zahlungsdienstleister <strong>Stripe</strong>
              (Stripe Payments Europe, Ltd., 1 Grand Canal Street Lower, Grand Canal Dock, Dublin,
              Irland; für technische Verarbeitung teils Stripe, Inc., 354 Oyster Point Blvd, South
              San Francisco, CA 94080, USA).
            </p>
            <p>
              Bei der Auswahl einer kostenpflichtigen Leistung werden Sie an die Zahlungsmaske von
              Stripe weitergeleitet. Zahlungsdaten (z. B. Kreditkartennummer) werden ausschließlich
              durch Stripe verarbeitet — wir erhalten diese Daten nicht und speichern sie nicht.
              Wir erhalten lediglich Informationen über den Zahlungsstatus und eine Transaktions-ID.
            </p>
            <p>
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).<br />
              <strong>Drittlandtransfer:</strong> Ein Transfer in die USA kann stattfinden. Stripe,
              Inc. ist unter dem EU-U.S. Data Privacy Framework zertifiziert.<br />
              <strong>Weitere Informationen:</strong>{' '}
              <a href="https://stripe.com/de/privacy" target="_blank" rel="noopener noreferrer">
                stripe.com/de/privacy
              </a>
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>9. Eingesetzte Dienste Dritter (Auftragsverarbeiter / Empfänger)</h2>

            <h3>9.1 Hosting — Vercel</h3>
            <p>
              Unsere Website wird gehostet bei der Vercel Inc., 340 S Lemon Ave #4133, Walnut, CA 91789,
              USA. Dabei werden technisch notwendige Daten (z. B. IP-Adresse, Request-Header) verarbeitet.
              Vercel ist unter dem EU-U.S. Data Privacy Framework zertifiziert; mit Vercel besteht ein
              Auftragsverarbeitungsvertrag.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO.{' '}
              <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
                vercel.com/legal/privacy-policy
              </a>
            </p>

            <h3>9.2 Datenbank, Authentifizierung und Storage — Supabase</h3>
            <p>
              Für Datenbank-, Auth- und Storage-Dienste nutzen wir Supabase (Supabase Inc., 970 Toa
              Payoh North, #07-04, Singapur 318992). Die Datenverarbeitung findet in einem EU-Rechen­
              zentrum in Frankfurt am Main statt. Mit Supabase besteht ein Auftragsverarbeitungsvertrag.
              <br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b und lit. f DSGVO.{' '}
              <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">
                supabase.com/privacy
              </a>
            </p>

            <h3>9.3 KI-Bildbearbeitung — Google Gemini</h3>
            <p>
              Zur virtuellen Möblierung und Bearbeitung der hochgeladenen Bilder setzen wir die Google
              Gemini API (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland; bzw.
              Google LLC, USA) ein. Hochgeladene Bilder werden zu diesem Zweck an Google übermittelt.
              Gemäß den API-Nutzungsbedingungen für Google werden diese Inhalte nicht zum Training der
              Google-Modelle verwendet.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).<br />
              <strong>Drittlandtransfer:</strong> Google LLC ist unter dem EU-U.S. Data Privacy
              Framework zertifiziert.{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                policies.google.com/privacy
              </a>
            </p>

            <h3>9.4 Bildhochskalierung — Freepik Magnific</h3>
            <p>
              Zur Erhöhung der Bildauflösung nutzen wir die Freepik Magnific Precision API (Freepik
              Company, S.L., Molina Lario 13, 29015 Málaga, Spanien). Die ausgewählten Bilder werden
              zur Verarbeitung an Freepik übermittelt. Die Verarbeitung findet innerhalb der EU statt.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).{' '}
              <a href="https://www.freepik.com/privacy" target="_blank" rel="noopener noreferrer">
                freepik.com/privacy
              </a>
            </p>

            <h3>9.5 Karten­dienst — Google Maps</h3>
            <p>
              Im Exposé-Editor binden wir Karten­kacheln, Autocomplete und Geocoding von Google Maps ein
              (Google Ireland Limited). Beim Aufruf werden technische Daten (z. B. IP-Adresse) an Google
              übermittelt. Die Nutzung erfolgt ausschließlich im Rahmen der Exposé-Erstellung durch den
              eingeloggten Nutzer.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).
            </p>

            <h3>9.6 Google Fonts</h3>
            <p>
              Wir setzen Schriftarten („Google Fonts", Outfit und Inter) ein, die derzeit von Google-Servern
              geladen werden können (Google Ireland Limited). Dabei kann Ihre IP-Adresse an Google
              übermittelt werden.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. f DSGVO (ein­heit­liche Darstellung von
              Schrift­arten).{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                policies.google.com/privacy
              </a>
            </p>

            <h3>9.7 Google Tag Manager</h3>
            <p>
              Wir verwenden den Google Tag Manager (Container-ID: GTM-K4C88NL5), mit dem Website-Tags
              zentral verwaltet werden. Der Google Tag Manager selbst setzt keine Cookies und erhebt
              keine personenbezogenen Daten, löst jedoch weitere Tags aus, die ihrerseits Daten
              erfassen können. Diese werden ausschließlich auf Basis einer Einwilligung aktiviert.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO i. V. m. § 25 Abs. 1 TDDDG.
            </p>

            <h3>9.8 Google Analytics 4 (GA4)</h3>
            <p>
              Über den Google Tag Manager kann Google Analytics 4 (Google Ireland Limited) geladen werden.
              GA4 verarbeitet dann pseudonymisierte Nutzungsdaten (insbesondere gekürzte IP-Adresse,
              Seitenaufrufe, Interaktionen, Geräte- und Browsertyp). Der Einsatz erfolgt ausschließlich
              nach Ihrer Einwilligung.<br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO i. V. m. § 25 Abs. 1 TDDDG.<br />
              <strong>Widerruf:</strong> Jederzeit über die Cookie-Einstellungen oder das offizielle
              Browser-Add-on von Google:{' '}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
                tools.google.com/dlpage/gaoptout
              </a>
            </p>

            <h3>9.9 Mixpanel (inkl. Session Replay)</h3>
            <p>
              Zur Produkt­analyse nutzen wir Mixpanel (Mixpanel, Inc., 1 Front St, Floor 28, San
              Francisco, CA 94111, USA). Die Daten­verarbeitung erfolgt über den EU-Endpunkt
              (api-eu.mixpanel.com). Erfasst werden unter anderem:
            </p>
            <ul>
              <li>pseudonyme Nutzer-Kennung und — sofern eingeloggt — Ihre User-ID, E-Mail und Name</li>
              <li>Klicks, Seitenaufrufe und Interaktionen („Autocapture")</li>
              <li>UTM-Parameter bei der ersten Ankunft auf der Seite</li>
              <li>
                bei einem Anteil von derzeit 10 % der Sitzungen: Session-Replays, d. h. Aufzeichnungen
                von Mauszeiger­bewegungen, Klicks und Seiten­inhalten (sensible Felder wie Passwort­
                eingaben werden maskiert)
              </li>
            </ul>
            <p>
              <strong>Zweck:</strong> Produkt­verbesserung, Analyse von Nutzungs­verhalten, Fehler­analyse.
              <br />
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO i. V. m. § 25 Abs. 1 TDDDG
              (Einwilligung).<br />
              <strong>Drittlandtransfer:</strong> Mixpanel, Inc. ist unter dem EU-U.S. Data Privacy
              Framework zertifiziert; zusätzlich nutzen wir den EU-Endpunkt. Mit Mixpanel besteht ein
              Auftragsverarbeitungsvertrag.<br />
              <strong>Speicherdauer:</strong> Bis zu 12 Monate, Session-Replays bis zu 30 Tage.{' '}
              <a href="https://mixpanel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
                mixpanel.com/legal/privacy-policy
              </a>
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>10. E-Mail-Kommunikation und Support</h2>
            <p>
              Wenn Sie uns per E-Mail kontaktieren (z. B. an info@estage.ai), verarbeiten wir Ihre
              Angaben zur Beantwortung Ihrer Anfrage.
            </p>
            <p>
              <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO, soweit die Anfrage auf einen
              Vertragsabschluss gerichtet ist, andernfalls Art. 6 Abs. 1 lit. f DSGVO (berechtigtes
              Interesse an der Beantwortung von Anfragen).<br />
              <strong>Speicherdauer:</strong> Die Daten werden gelöscht, sobald sie für die Bearbeitung
              nicht mehr erforderlich sind und keine gesetzlichen Aufbewahrungspflichten entgegenstehen.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>11. Datenübermittlung in Drittländer</h2>
            <p>
              Soweit wir in dieser Erklärung Dienstleister in Drittländern (insbesondere den USA)
              einsetzen, erfolgt die Datenübermittlung ausschließlich auf Basis geeigneter Garantien
              gemäß Art. 44 ff. DSGVO — namentlich:
            </p>
            <ul>
              <li>
                einer Zertifizierung unter dem <strong>EU-U.S. Data Privacy Framework</strong> (z. B.
                Google, Stripe, Mixpanel, Vercel) und/oder
              </li>
              <li>
                <strong>EU-Standard­vertrags­klauseln</strong> (Art. 46 Abs. 2 lit. c DSGVO), ggf. ergänzt
                um zusätzliche technische und organisatorische Maßnahmen.
              </li>
            </ul>
          </section>

          <section className="lp-legal-section">
            <h2>12. Rechte der betroffenen Person</h2>
            <p>
              Werden Ihre personenbezogenen Daten verarbeitet, sind Sie Betroffener i. S. d. DSGVO und
              Ihnen stehen die folgenden Rechte gegenüber dem Verantwortlichen zu:
            </p>
            <ul>
              <li><strong>Auskunftsrecht</strong> (Art. 15 DSGVO)</li>
              <li><strong>Recht auf Berichtigung</strong> (Art. 16 DSGVO)</li>
              <li><strong>Recht auf Löschung</strong> („Recht auf Vergessenwerden", Art. 17 DSGVO)</li>
              <li><strong>Recht auf Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)</li>
              <li><strong>Recht auf Datenübertragbarkeit</strong> (Art. 20 DSGVO)</li>
              <li>
                <strong>Widerspruchsrecht</strong> gegen die Verarbeitung, die auf Art. 6 Abs. 1 lit. e
                oder f DSGVO beruht (Art. 21 DSGVO)
              </li>
              <li>
                <strong>Widerrufsrecht</strong> bei erteilter Einwilligung mit Wirkung für die Zukunft
                (Art. 7 Abs. 3 DSGVO)
              </li>
            </ul>
            <p>
              Zur Ausübung dieser Rechte genügt eine formlose Nachricht an{' '}
              <a href="mailto:info@estage.ai">info@estage.ai</a>.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>13. Beschwerderecht bei der Aufsichtsbehörde</h2>
            <p>
              Unbeschadet eines anderweitigen verwaltungsrechtlichen oder gerichtlichen Rechtsbehelfs
              steht Ihnen das Recht auf Beschwerde bei einer Datenschutzaufsichtsbehörde zu. Für uns
              örtlich zuständig ist:
            </p>
            <p>
              Sächsischer Datenschutzbeauftragter<br />
              Devrientstraße 5<br />
              01067 Dresden<br />
              Telefon: +49 (0) 351 / 85471-101<br />
              E-Mail:{' '}
              <a href="mailto:saechsdsb@slt.sachsen.de">saechsdsb@slt.sachsen.de</a><br />
              Web:{' '}
              <a
                href="https://www.datenschutz.sachsen.de"
                target="_blank"
                rel="noopener noreferrer"
              >
                www.datenschutz.sachsen.de
              </a>
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>14. Datensicherheit</h2>
            <p>
              Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um Ihre Daten gegen
              zufällige oder vorsätzliche Manipulation, Verlust, Zerstörung oder den Zugriff Unberechtigter
              zu schützen. Alle Übertragungen zwischen Ihrem Browser und unseren Servern erfolgen
              verschlüsselt (TLS/HTTPS). Unsere Sicherheitsmaßnahmen werden regelmäßig überprüft und
              dem technischen Fortschritt angepasst.
            </p>
          </section>

          <section className="lp-legal-section">
            <h2>15. Aktualität und Änderung dieser Datenschutzerklärung</h2>
            <p>
              Diese Datenschutzerklärung hat den Stand April 2026. Durch die Weiter­entwicklung unserer
              Website und Angebote oder aufgrund geänderter gesetzlicher bzw. behördlicher Vorgaben
              kann es notwendig werden, diese Datenschutzerklärung zu ändern. Die jeweils aktuelle
              Fassung kann jederzeit auf der Website unter{' '}
              <a href="/datenschutz" onClick={(e) => e.preventDefault()}>
                estage.ai/datenschutz
              </a>{' '}
              abgerufen werden.
            </p>
          </section>

          {onImpressumClick && (
            <section className="lp-legal-section">
              <p>
                Siehe auch:{' '}
                <a
                  href="/impressum"
                  onClick={(e) => { e.preventDefault(); onImpressumClick(); }}
                >
                  Impressum
                </a>
                .
              </p>
            </section>
          )}
        </div>
      </div>
      <FooterSection
        onNavigateHome={handleNavigateHome}
        onBlogClick={onBlogClick}
        onImpressumClick={onImpressumClick}
      />
    </div>
  );
};
